import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import InfoTooltip from '../../../common_components/InfoTooltip';
import CommonTable from '../../../common_components/CommonTable';
import { EXPAND_TRIGGERS } from '../../../common_components/tableConstants';
import TableToolbar from '../../../common_components/TableToolbar';
import CampaignPreviewDrawer from '../../EmailMarketingReport/CampaignPreviewDrawer';
import EventDrawer from '../../MarketingCalender/AdvanceCalender/components/EventDrawer';
import { Dropdown } from '../../../common_components/FormInputs';
import Pagination from '../../../common_components/Pagination';
import { useSelector } from 'react-redux';
import { selectUser, selectEntityType } from '../../../redux/authSlice';
const CHIP_STYLES = {
    pending: { bg: "#FEFCE8", color: "#936316" },
    approved: { bg: "#ECFDF3", color: "#027A48", label: "Approved" },
    rejected: { bg: "#FEF2F2", color: "#B42318", label: "Not Approved" },
};
const getChipConfig = (status) => {
    const s = status?.toLowerCase?.() || "";
    if (s === "approved")
        return CHIP_STYLES.approved;
    if (s === "rejected" || s === "not_approved")
        return CHIP_STYLES.rejected;
    return { ...CHIP_STYLES.pending, label: "Waiting for Approval" };
};
const EmailStatusTab = ({ data, clientId }) => {
    const user = useSelector(selectUser);
    const entityType = useSelector(selectEntityType);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState(null);
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const limit = 20;
    React.useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter, sortConfig]);
    const statusOptions = [
        { value: 'All', label: 'All Statuses' },
        { value: 'Approved', label: 'Approved' },
        { value: 'Waiting for Approval', label: 'Waiting for Approval' }
    ];
    const filteredData = useMemo(() => {
        if (!data)
            return [];
        let result = data;
        if (statusFilter !== 'All') {
            result = result.filter(row => getChipConfig(row.status).label === statusFilter);
        }
        if (searchTerm.trim()) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(row => {
                const name = (row.campaign_name || row.name || "").toLowerCase();
                return name.includes(lowerTerm);
            });
        }
        return result;
    }, [data, searchTerm, statusFilter]);
    const sortedData = useMemo(() => {
        if (!sortConfig?.columnId)
            return filteredData;
        const { columnId, direction } = sortConfig;
        return [...filteredData].sort((a, b) => {
            const aVal = (a[columnId] ?? '').toString().toLowerCase();
            const bVal = (b[columnId] ?? '').toString().toLowerCase();
            if (aVal < bVal)
                return direction === 'asc' ? -1 : 1;
            if (aVal > bVal)
                return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig]);
    const paginatedData = useMemo(() => {
        const startIndex = (page - 1) * limit;
        return sortedData.slice(startIndex, startIndex + limit);
    }, [sortedData, page, limit]);
    const columns = [
        { id: "campaign_name", label: "Campaign Name", sortable: true, minWidth: 260 },
        { id: "preview", label: "Preview", sortable: false, minWidth: 80, align: "center" },
        { id: "status", label: "Sent Status", sortable: true, minWidth: 160 },
    ];
    const renderCell = (row, column) => {
        if (column.id === "status") {
            const chip = getChipConfig(row.status);
            return (_jsx(Box, { sx: {
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    p: "4px 12px", borderRadius: "999px", fontSize: 14, fontWeight: 500,
                    minWidth: 150, textAlign: "center", backgroundColor: chip.bg, color: chip.color
                }, children: chip.label }));
        }
        if (column.id === "preview") {
            return (_jsx(Box, { component: "button", type: "button", sx: { border: "none", backgroundColor: "transparent", cursor: "pointer", p: 0 }, onClick: () => {
                    setSelectedRow(row);
                    setPreviewOpen(true);
                }, children: _jsx("img", { src: "/assets/eye.svg", alt: "preview" }) }));
        }
        return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: row[column.id] || "—" });
    };
    if (!data || data.length === 0) {
        return _jsx(Typography, { sx: { p: 2, color: '#667085' }, children: "Email performance metrics are unavailable for the selected date range." });
    }
    return (_jsxs(Box, { sx: { width: '100%', border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsx(TableToolbar, { title: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '18px', fontWeight: 600, color: '#101828' }, children: "Email Status" }), _jsx(InfoTooltip, { title: "This data is for the last 2 months", placement: "top", size: 18, sx: { mb: '2px' } })] }), showSearch: true, searchValue: searchTerm, onSearchChange: setSearchTerm, searchPlaceholder: "Search by campaign", showFullScreen: true, customActionsLeft: _jsx(Box, { sx: { width: 160 }, children: _jsx(Dropdown, { value: statusFilter, onChange: (val) => setStatusFilter(val), options: statusOptions, size: "sm", fullWidth: true, hideCheckIcon: true }) }) }), _jsxs(Box, { className: "user-list-table-wrapper", children: [_jsx(CommonTable, { columns: columns, rows: paginatedData.map((r, i) => ({ ...r, id: r._id || i })), renderCell: renderCell, expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: sortConfig ? { columnId: sortConfig.columnId, direction: sortConfig.direction } : undefined, onSortChange: (id, dir) => setSortConfig(dir ? { columnId: id, direction: dir } : null), stickyHeader: true, maxHeight: 400 }), sortedData.length > 0 && (_jsx(Pagination, { currentPage: page, totalPages: Math.ceil(sortedData.length / limit), onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedData.length, itemsPerPage: limit }))] }), previewOpen && selectedRow && clientId && (selectedRow.type === 'proposal' ? (_jsx(EventDrawer, { open: previewOpen, onClose: () => {
                    setPreviewOpen(false);
                    setSelectedRow(null);
                }, event: { extendedProps: { ...selectedRow, type: 'proposal', isNewEvent: false } }, clientId: clientId, selectedMonth: "", connectionId: selectedRow.connection_id, user: user, entityType: entityType, readOnlyInfo: true })) : (_jsx(CampaignPreviewDrawer, { open: previewOpen, onClose: () => {
                    setPreviewOpen(false);
                    setSelectedRow(null);
                }, rowData: selectedRow, channelKey: "campaigns", clientId: clientId, connectionId: selectedRow.connection_id })))] }));
};
export default EmailStatusTab;
